import { cn } from "@babackapp/customer-ui/lib/utils";
import type { SuggestionItem } from "../../hooks/use-resolution-suggestions";
import { ArrowUpRight, Ruler, Sparkles } from "lucide-react";
import { motion } from "motion/react";

/** Last chip of the card: leaves the suggestions for the full picker. */
export type SuggestionMoreAction = {
  label: string;
  onClick: () => void;
};

type Props = {
  title: string;
  items: SuggestionItem[];
  onSelect: (item: SuggestionItem) => void;
  moreAction?: SuggestionMoreAction;
};

// `min-h-12` is the height of a size chip (two lines): the "Other sizes…"
// chip, one line, keeps it when it wraps alone onto its own row.
const CHIP_CLASS_NAME = cn(
  "group flex min-h-12 flex-row items-center gap-2 rounded-lg border bg-background cursor-pointer",
  "p-1 text-left transition-colors",
  "hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
);

function SuggestionTile({
  item,
  onSelect,
}: {
  item: SuggestionItem;
  onSelect: (item: SuggestionItem) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className={CHIP_CLASS_NAME}
    >
      <div className="size-6 overflow-hidden rounded-md bg-muted shrink-0">
        {item.imageUrl ? (
          <motion.img
            layoutId={`suggestion-image-${item.id}`}
            src={item.imageUrl}
            alt={item.subtitle}
            className="size-full object-cover"
          />
        ) : (
          <motion.div
            layoutId={`suggestion-image-${item.id}`}
            className="size-full bg-muted"
          />
        )}
      </div>
      <div className=" min-w-0 text-sm flex flex-col">
        <p className="text-muted-foreground">{item.label}</p>
        <p className="truncate font-medium leading-tight text-foreground">
          {item.subtitle}
        </p>
      </div>
      <ChipArrow />
    </button>
  );
}

function ChipArrow() {
  return (
    <ArrowUpRight
      className="stroke-1 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-active:-translate-x-0.5 group-active:translate-y-0.5 size-3 shrink-0"
      aria-hidden
    />
  );
}

function MoreTile({ action }: { action: SuggestionMoreAction }) {
  return (
    // Capped near a size chip's width: a long translation wraps onto a
    // second line instead of widening the chip.
    <button
      type="button"
      onClick={action.onClick}
      className={cn(CHIP_CLASS_NAME, "max-w-36")}
    >
      <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <Ruler className="size-4" aria-hidden />
      </div>
      <span className="min-w-0 text-sm font-medium leading-tight text-foreground">
        {action.label}
      </span>
      <ChipArrow />
    </button>
  );
}

export function SuggestionGrid({ title, items, onSelect, moreAction }: Props) {
  if (items.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes ai-border-shift {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
      <div
        className="rounded-xl p-[2.5px]"
        style={{
          background:
            "linear-gradient(135deg, #c4b5fd, #fdba74, #86efac, #c4b5fd, #fdba74)",
          backgroundSize: "300% 300%",
          animation: "ai-border-shift 5s ease infinite",
        }}
      >
        <div className="flex flex-col gap-3 rounded-[10px] bg-white p-3">
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium text-foreground">
              <Sparkles className="size-4 text-violet-300 inline mr-1 -translate-y-0.5" />
              {title}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {items.map((item) => (
              <SuggestionTile key={item.id} item={item} onSelect={onSelect} />
            ))}
            {moreAction && <MoreTile action={moreAction} />}
          </div>
        </div>
      </div>
    </>
  );
}
